import _ from 'lodash'

document.addEventListener('alpine:init', () => {
    Alpine.data('carbonCalculator', () => ({
        active: '',
        modifyWidth: false,
        delay: 0,
        init() {
            if (window.location.hash === '#fuel-saver') {
                this.switchTab('fuel')
            } else {
                this.switchTab('carbon')
            }
        },

        switchTab(targetTab) {
            this.$dispatch('reset_' + targetTab)
            this.active = targetTab
            this.modifyWidth = true
            clearTimeout(this.delay)
            this.delay = setTimeout(() => this.modifyWidth = false, 250)
        },

        formatNumber(n) {
            return this.numericResult(n)
                .toString()
                .replace(/(\d)(?=(\d{3})+(\.\d+)?$)/g, '$1,')
        },

        numericResult(r) {
            if (r) {
                if (_.isString(r)) {
                    return parseFloat(r.replace(/[^0-9\.]/g, ''))
                }

                return parseFloat(r)
            }
            return 0
        },

        displayResult(modelName, suffix = null, prefix = null) {
            const newDisplay = (prefix ?? '') + this.formatNumber(this[modelName]) + (suffix ?? '')

            if (newDisplay !== this[modelName]) {
                this[modelName] = newDisplay
            }
        },

        mask: function (input, maxLength = 2, decimal = 0) {
            let n = this.numericResult(input).toString().split('.')
            let dec = (decimal && (input.at(-1) === '.' || n.length > 1))

            let mask = ''
            if (dec) {
                mask = '9'.repeat(n[0].length)
            } else if (n[0].length < maxLength) {
                mask = '9'.repeat(n[0].length + 1)
            } else {
                mask = '9'.repeat(maxLength)
            }

            mask = this.formatNumber(mask)

            if (decimal) {
                let dMask = ''
                if (n[1] && n[1].length < decimal) {
                    dMask = '9'.repeat(n[1].length + 1)
                } else {
                    dMask = '9'.repeat(decimal)
                }
                mask += '.' + dMask
            }

            return mask
        }
    }))

    Alpine.data('carbonEmissionCalculator', (options) => ({
        energyEmission: options.energyEmission ?? 149,
        gasEmission: options.gasEmission ?? 197,
        fuelEmission: options.fuelEmission ?? 220,

        init() {
            window.addEventListener('reset_carbon', () => {
                this.resetValues()
            })
        },

        fuelType: 0,
        userElectricityUsage: null,
        userGasUsage: null,
        userVehicleUsage: null,
        resetValues() {
            this.fuelType = 0
            this.userElectricityUsage = this.userGasUsage = this.userVehicleUsage = null
        },

        get electricityUsageEmissions() {
            let e = this.numericResult(this.userElectricityUsage)

            if (_.isNumber(e)) {
                return Math.round(((this.energyEmission * e) * 12) / 1000)
            }

            return 0
        },

        get gasUsageEmissions() {
            let g = this.numericResult(this.userGasUsage)
            if (_.isNumber(g)) {
                return Math.round(((this.gasEmission * g) * 12) / 1000)
            }

            return 0
        },

        get vehicleUsageEmissions() {
            let v = this.numericResult(this.userVehicleUsage)
            if (_.isNumber(v) && this.fuelType !== 2) {
                return Math.round((this.fuelEmission * v) / 1000)
            }

            return 0
        },

        get totalUsageEmissions() {
            return this.electricityUsageEmissions + this.gasUsageEmissions + this.vehicleUsageEmissions
        }
    }))

    Alpine.data('fuelSaverCalculator', (options) => ({
        fuelRoadUserCharges: options.fuelRoadUserCharges ?? 0,
        dieselRoadUserCharges: options.dieselRoadUserCharges ?? 0.076,
        evRoadUserCharges: options.evRoadUserCharges ?? 0.076,
        phevRoadUserCharges: options.phevRoadUserCharges ?? 0.038,

        fuelMaintenance: options.fuelMaintenance ?? 0,
        dieselMaintenance: options.dieselMaintenance ?? 0,
        evMaintenance: options.evMaintenance ?? 300,

        petrolPricePerLitre: options.petrolPricePerLitre ?? 2.912,
        dieselPricePerLitre: options.dieselPricePerLitre ?? 1.891,
        costPerKWH: options.costPerKWH ?? 0.3251,
        evModifier: options.evModifier ?? 0.16,
        defaultFuelEconomy: options.defaultFuelEconomy ?? 7.6,
        phevFuelEconomy: options.phevFuelEconomy ?? 2,

        fuelType: 0,
        userTravelDistance: null,
        userPricePerLitre: null,
        userMaintenanceCost: null,
        userFuelEconomy: null,

        init() {
            window.addEventListener('reset_fuel', () => {
                this.resetValues()
            })
        },

        resetValues() {
            this.fuelType = this._fuelCostsPerYear = this._evCostsPerYear = 0
            this.userTravelDistance = this.userPricePerLitre = this.userMaintenanceCost = this.userFuelEconomy = null
        },

        get travelDistance() {
            if (!this.userTravelDistance) return 0
            return this.numericResult(this.userTravelDistance)
        },

        _fuelCostsPerYear: 0,
        get fuelCostsPerYear() {
            if (this.fuelType === 2) {
                return 0
            }

            const RUC = this.travelDistance * this.roadUserCharges
            const estLitresCost = ((this.travelDistance * this.fuelEconomy) * this.pricePerLitre) / 100

            this._fuelCostsPerYear = Math.round(RUC + estLitresCost + this.vehicleMaintenanceCost)
            return this.formatNumber(this._fuelCostsPerYear)
        },

        get fuelEconomy() {
            if (this.userFuelEconomy) return this.numericResult(this.userFuelEconomy)
            if (this.fuelType === 3) return this.numericResult(this.phevFuelEconomy)
            return this.defaultFuelEconomy
        },

        get pricePerLitre() {
            if (this.userPricePerLitre) return this.numericResult(this.userPricePerLitre)

            if (this.fuelType === 0 || this.fuelType === 3) return this.petrolPricePerLitre

            if (this.fuelType === 1) return this.dieselPricePerLitre

            return 0
        },

        get roadUserCharges() {
            if (this.fuelType === 0) return this.fuelRoadUserCharges

            if (this.fuelType === 1) return this.dieselRoadUserCharges

            if (this.fuelType === 2) return this.evRoadUserCharges

            if (this.fuelType === 3) return this.phevRoadUserCharges

            return 0
        },

        get vehicleMaintenanceCost() {
            if (this.userMaintenanceCost) return this.numericResult(this.userMaintenanceCost)

            if (this.fuelType === 0 || this.fuelType === 3) return this.fuelMaintenance

            if (this.fuelType === 1) return this.dieselMaintenance

            return 0
        },

        _evCostsPerYear: 0,
        get evCostsPerYear() {
            const RUC = this.travelDistance * this.evRoadUserCharges
            const estPowerCost = this.travelDistance * this.costPerKWH

            this._evCostsPerYear = Math.round((RUC + estPowerCost) * this.evModifier + this.evMaintenance)
            return this.formatNumber(this._evCostsPerYear)
        },

        get evSavingsPerYear() {
            if (this.fuelType === 2) return '-'

            const savings = Math.round(this._fuelCostsPerYear - this._evCostsPerYear)
            if (savings > 0) {
                return savings
            }
            return '-'
        },

        get evSavingsPerYearPercent() {
            if (this.evSavingsPerYear !== '-') {
                return Math.round(this.evSavingsPerYear / this._fuelCostsPerYear * 100)
            }

            return '-'
        }
    }))
})
